import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardEvents } from '../contexts/EventContext';
import * as dashboardService from '../services/dashboardService';
import * as recommendationsService from '../services/recommendationsService';
import * as eventsService from '../services/eventsService';
import * as homeworkService from '../services/homeworkService';
import * as profileService from '../services/profileService';
import * as orientationRegistrationsService from '../services/orientationRegistrationsService';
import RecommendedEvents from '../components/Student/RecommendedEvents';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { resolveEventTimeZone, formatSessionDateTimeWithZone } from '../utils/sessionTime';

const { FiCalendar, FiClock, FiCheck, FiPlay, FiBookOpen, FiArrowRight, FiAlertCircle } = FiIcons;

function formatSessionTime(iso, event) {
  if (!iso) return '—';
  return formatSessionDateTimeWithZone(iso, resolveEventTimeZone(event));
}

export default function StudentDashboard() {
  const { profile, displayName, authReady } = useAuth();
  const { events: registeredEvents = [], loading: eventsLoading } = useDashboardEvents();

  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [publicEvents, setPublicEvents] = useState([]);
  const [agentRefCode, setAgentRefCode] = useState(null);
  const [studentHomework, setStudentHomework] = useState([]);
  const [incompleteOrientationRegistrations, setIncompleteOrientationRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  const studentId = profile?.id ?? null;

  useEffect(() => {
    if (!authReady || !studentId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        await orientationRegistrationsService.claimOrientationRegistrationsByEmail();

        const [sessionsRes, recRes, publicRes, hwRes, orientationRes] = await Promise.all([
          dashboardService.getUpcomingSessionsForStudent(studentId, 7),
          recommendationsService.getStudentRecommendations(studentId, { role: 'student', userId: studentId }),
          eventsService.getPublicEvents({ limit: 12 }),
          homeworkService.listStudentHomework(studentId, { role: 'student', userId: studentId }),
          orientationRegistrationsService.listOrientationRegistrations({
            role: 'student',
            userId: studentId,
            completedFullRegistration: false
          })
        ]);

        if (cancelled) return;
        setUpcomingSessions(sessionsRes.data ?? []);
        setRecommendations(recRes.data ?? []);
        setPublicEvents(publicRes.data ?? []);
        setStudentHomework(hwRes.data ?? []);
        setIncompleteOrientationRegistrations(orientationRes.data ?? []);
      } catch (e) {
        console.error('[StudentDashboard] load error', e);
      }

      const agentId = profile.parent_agent_id;
      if (agentId) {
        const { data: code } = await profileService.getAgentReferralCode(agentId);
        if (!cancelled) setAgentRefCode(code ?? null);
      }
      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [authReady, studentId, profile?.parent_agent_id]);

  const totalRegistered = registeredEvents.length;
  const pendingHomework = studentHomework.filter(
    (h) => (h.submission?.status ?? 'pending') !== 'graded' && (h.submission?.status ?? 'pending') !== 'submitted'
  ).length;
  const completedSessions = 0;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Welcome back, {displayName}!</h1>
        <p className="text-slate-600 mt-1">Here’s your overview and recommended events</p>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Events registered</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{totalRegistered}</p>
            </div>
            <div className="rounded-lg bg-[#00AAFF]/10 p-3">
              <SafeIcon icon={FiCalendar} className="w-6 h-6 text-[#00AAFF]" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Completed sessions</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{completedSessions}</p>
            </div>
            <div className="rounded-lg bg-emerald-500/10 p-3">
              <SafeIcon icon={FiCheck} className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Pending homework</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{pendingHomework}</p>
            </div>
            <div className="rounded-lg bg-amber-500/10 p-3">
              <SafeIcon icon={FiBookOpen} className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Incomplete registrations (orientation only – complete full registration) */}
      {incompleteOrientationRegistrations.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-amber-600" />
            Incomplete registrations
          </h2>
          <p className="text-sm text-slate-600 mt-1 mb-4">
            You’re registered for the free orientation. Complete your registration to access all sessions and materials.
          </p>
          <ul className="space-y-3">
            {incompleteOrientationRegistrations.map((orReg) => (
              <li
                key={orReg.id}
                className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-lg border border-amber-100 p-4"
              >
                <div>
                  <p className="font-medium text-slate-900">{orReg.event?.title ?? 'Event'}</p>
                  <p className="text-sm text-slate-500">{orReg.email}</p>
                </div>
                <Link
                  to={`/complete-registration/${orReg.event_id}?leadId=${orReg.lead_id}&email=${encodeURIComponent(orReg.email || '')}`}
                  className="inline-flex items-center gap-1 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
                >
                  Complete registration
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Upcoming sessions (next 7 days) */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Upcoming sessions</h2>
          <Link
            to="/dashboard/my-events"
            className="text-sm font-medium text-[#00AAFF] hover:underline inline-flex items-center gap-1"
          >
            My Events <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
          </Link>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : upcomingSessions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <SafeIcon icon={FiClock} className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No sessions in the next 7 days</p>
              <p className="text-sm mt-1">Register for events to see sessions here</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {upcomingSessions.slice(0, 5).map((session) => (
                <li key={session.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="font-medium text-slate-900">
                      {session.title || session.session_title || `Session ${session.session_number ?? ''}`}
                    </p>
                    <p className="text-sm text-slate-500">
                      {session.event?.title} · {formatSessionTime(session.start_time, session.event)}
                    </p>
                  </div>
                  <Link
                    to={`/dashboard/student/events/${session.event_id}`}
                    className="text-sm font-medium text-[#00AAFF] hover:underline"
                  >
                    View event
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recommended events */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Recommended events</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Register with your agent’s link so they get credit
          </p>
        </div>
        <div className="p-6">
          <RecommendedEvents
            recommendations={recommendations}
            publicEvents={publicEvents}
            agentRefCode={agentRefCode}
            loading={loading}
            emptyMessage="No recommended or public events right now."
          />
        </div>
      </div>
    </div>
  );
}
